import { Component } from '@theme/component';
import { CartItemsComponent } from '@theme/component-cart-items';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

export const upsellState = { instances: new Set(), pending: new Set(), cart: null, active: false, uncertain: false, error: '', status: '', pendingUntil: 0, generation: 0 };
const syncAll = () => upsellState.instances.forEach((instance) => instance.render());
const eligible = (cart, productId) => (cart?.items || []).filter((item) => String(item.product_id) === String(productId));
export const eligibleQuantity = (cart, productId) => eligible(cart, productId).reduce((sum, item) => sum + Number(item.quantity), 0);
export const pairTotal = (a, b) => Math.max(a, b);
const signature = (items) => JSON.stringify(items.map((item) => ({ id: String(item.variant_id || item.id), quantity: Number(item.quantity), properties: Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)), plan: item.selling_plan_allocation?.selling_plan?.id || null })).sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b))));

export function inspectUpsell(cart, before, variant, attempt, productId, title) {
  const added = (cart?.items || []).filter((item) => item.properties?._barevon_cart_attempt === attempt);
  const originals = (cart?.items || []).filter((item) => item.properties?._barevon_cart_attempt !== attempt);
  const pairs = eligible(cart, productId);
  const first = eligible(before, productId)[0];
  const titles = pairs.flatMap((item) => [...(item.discounts || []).map((d) => d.title), ...(item.line_level_discount_allocations || []).map((d) => d.discount_application?.title), ...(item.discount_allocations || []).map((d) => d.discount_application?.title)]);
  const total = pairs.reduce((sum, item) => sum + Number(item.final_line_price), 0);
  const expected = pairTotal(Number(first?.original_price ?? first?.price), Number(variant.price));
  return { added, valid: added.length === 1 && Number(added[0].quantity) === 1 && String(added[0].variant_id) === String(variant.id) && eligibleQuantity(cart, productId) === 2 && signature(originals) === signature(before.items) && Number.isFinite(total) && total === expected && titles.includes(title) };
}

function onCartEvent(event) {
  if (!event.promise) return;
  const promise = event.promise;
  const generation = ++upsellState.generation;
  upsellState.pending.add(promise);
  syncAll();
  promise.then(async ({ detail }) => {
    const fullCartSources = ['cart-items-component', 'product-form-component', 'barevon-studio-offer', 'barevon-cart-upsell'];
    if (Array.isArray(detail?.items) && fullCartSources.includes(detail.source)) {
      if (generation === upsellState.generation) upsellState.cart = { ...upsellState.cart, items: detail.items };
    } else {
      const instance = [...upsellState.instances][0];
      if (!instance) return;
      try {
        const cart = await instance.request('cart.js');
        if (generation === upsellState.generation) upsellState.cart = cart;
      } catch {
        if (generation === upsellState.generation) upsellState.cart = null;
      }
    }
  }).catch(() => {}).finally(() => { upsellState.pending.delete(promise); syncAll(); });
}
document.addEventListener(StandardEvents.cartLinesUpdate, onCartEvent);
document.addEventListener(StandardEvents.cartDiscountUpdate, onCartEvent);
document.addEventListener(ThemeEvents.quantitySelectorUpdate, () => {
  upsellState.pendingUntil = Date.now() + 350;
  syncAll();
  setTimeout(syncAll, 360);
});

export class BarevonCartUpsell extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.controller = new AbortController();
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-upsell-color]')) this.color = event.target.value;
      if (event.target.matches('[data-upsell-size]')) this.size = event.target.value;
      this.render();
    }, { signal: this.controller.signal });
    this.addEventListener('click', (event) => { if (event.target.closest('[data-upsell-add]')) this.addPair(); }, { signal: this.controller.signal });
    upsellState.instances.add(this);
    this.loadConfig();
  }

  updatedCallback() {
    super.updatedCallback();
    this.loadConfig();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.controller?.abort();
    upsellState.instances.delete(this);
  }

  loadConfig() {
    try {
      this.config = JSON.parse(this.querySelector('[data-upsell-config]').textContent);
      this.variants = this.config.variants;
      this.colorIndex = this.config.options.findIndex((name) => /^(color|colour)$/i.test(name));
      this.sizeIndex = this.config.options.findIndex((name) => /^size$/i.test(name));
      if (!Array.isArray(this.variants) || this.colorIndex < 0 || this.sizeIndex < 0) throw new Error('Invalid options');
      if (!upsellState.active) upsellState.cart = this.config.cart;
      const first = eligible(upsellState.cart, this.config.productId)[0];
      const initial = this.variants.find((variant) => variant.available && String(variant.id) === String(first?.variant_id)) || this.variants.find((variant) => variant.available);
      this.color ||= initial?.options[this.colorIndex];
      this.size ||= initial?.options[this.sizeIndex];
      this.render();
    } catch { this.hidden = true; }
  }

  money(cents) {
    if (['GBP', 'CAD', 'AUD'].includes(this.config.currency)) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.config.currency }).format(cents / 100);
    }
    return new DOMParser().parseFromString(formatMoney(cents, this.config.moneyFormat, this.config.currency), 'text/html').body.textContent;
  }

  selectedVariant() {
    return this.variants?.find((variant) => variant.options[this.colorIndex] === this.color && variant.options[this.sizeIndex] === this.size);
  }

  busy() {
    return upsellState.active || upsellState.uncertain || upsellState.pending.size > 0 || Date.now() < upsellState.pendingUntil;
  }

  render() {
    if (!this.config || !this.querySelector('[data-upsell-color]')) return;
    const qualifies = eligibleQuantity(upsellState.cart, this.config.productId) === 1 && this.variants.some((variant) => variant.available);
    this.hidden = !qualifies && !upsellState.error;
    this.querySelector('[data-upsell-controls]').hidden = !qualifies || upsellState.uncertain;
    const colorSelect = this.querySelector('[data-upsell-color]');
    const sizeSelect = this.querySelector('[data-upsell-size]');
    const colors = [...new Set(this.variants.map((variant) => variant.options[this.colorIndex]))];
    colorSelect.replaceChildren(...colors.map((color) => {
      const option = new Option(color, color, false, color === this.color);
      option.disabled = !this.variants.some((variant) => variant.options[this.colorIndex] === color && variant.available);
      return option;
    }));
    const variants = this.variants.filter((variant) => variant.options[this.colorIndex] === this.color);
    if (!variants.some((variant) => variant.options[this.sizeIndex] === this.size)) this.size = variants.find((variant) => variant.available)?.options[this.sizeIndex] || variants[0]?.options[this.sizeIndex];
    sizeSelect.replaceChildren(...variants.map((variant) => {
      const size = variant.options[this.sizeIndex];
      const label = this.config.sizeLabels[size] || `Size ${size}`;
      const option = new Option(label + (variant.available ? '' : ` · ${this.config.strings.sold_out}`), size, false, size === this.size);
      option.disabled = !variant.available;
      return option;
    }));
    const selected = this.selectedVariant();
    const first = eligible(upsellState.cart, this.config.productId)[0];
    const firstPrice = Number(first?.original_price ?? first?.price ?? selected?.price);
    const marginalPrice = selected ? pairTotal(firstPrice, selected.price) - firstPrice : 0;
    this.querySelector('[data-upsell-regular]').textContent = this.money(selected?.price || 0);
    this.querySelector('[data-upsell-price]').textContent = this.money(marginalPrice);
    const image = this.querySelector('[data-upsell-image]');
    const source = selected?.featured_image?.src || selected?.featured_media?.preview_image?.src || variants.find((variant) => variant.featured_image?.src)?.featured_image.src;
    if (source) { image.src = source; image.hidden = false; } else image.hidden = true;
    image.alt = `${this.config.productTitle} · ${this.color || ''}`;
    colorSelect.disabled = sizeSelect.disabled = this.busy();
    const button = this.querySelector('[data-upsell-add]');
    button.disabled = this.busy() || !qualifies || !selected?.available;
    button.textContent = marginalPrice === 0 ? this.config.strings.button : `${this.config.strings.button} · ${this.money(marginalPrice)}`;
    this.setAttribute('aria-busy', String(upsellState.active));
    this.querySelector('[data-upsell-status]').textContent = upsellState.status;
    const error = this.querySelector('[data-upsell-error]');
    error.textContent = upsellState.error;
    error.hidden = !upsellState.error;
    this.querySelector('[data-upsell-review]').hidden = !upsellState.uncertain;
  }

  endpoint(path) { return `${String(this.config.rootUrl || '/').replace(/\/?$/, '/')}${path}`; }

  async request(path, body) {
    const response = await fetch(this.endpoint(path), { method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000), headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) }, ...(body ? { body: JSON.stringify(body) } : {}) });
    const data = await response.json();
    if (!response.ok || data.status || data.errors) {
      const error = new Error(data.description || data.message || String(data.errors || this.config.strings.failed));
      error.serverRejected = true;
      throw error;
    }
    return data;
  }

  sections() { return [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((element) => element.dataset.sectionId))].slice(0, 5); }
  sectionPayload() { return { sections: this.sections(), sections_url: location.pathname + location.search }; }

  lock() {
    this.locks = new Map();
    const apply = () => {
      for (const element of document.querySelectorAll('cart-items-component, product-form-component, barevon-studio-offer, sticky-add-to-cart')) {
        if (!this.locks.has(element)) this.locks.set(element, element.inert);
        element.inert = true;
      }
    };
    apply();
    this.lockObserver = new MutationObserver(apply);
    this.lockObserver.observe(document.body, { childList: true, subtree: true });
  }

  unlock() {
    this.lockObserver?.disconnect();
    for (const [element, value] of this.locks || []) element.inert = value;
    this.locks?.clear();
    if (upsellState.uncertain) {
      const blockCheckout = () => document.querySelectorAll('[name="checkout"], .cart__dynamic-checkout-buttons, .additional-checkout-buttons, shopify-accelerated-checkout-cart').forEach((element) => { element.inert = true; element.setAttribute('aria-disabled', 'true'); if (element.matches('[name="checkout"]')) element.disabled = true; });
      blockCheckout();
      this.checkoutObserver = new MutationObserver(blockCheckout);
      this.checkoutObserver.observe(document.body, { childList: true, subtree: true });
    }
  }

  resolve(deferred, cart, sections, didError) {
    upsellState.cart = cart;
    deferred?.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, itemCount: cart.item_count, sections, didError, source: 'barevon-cart-upsell', sourceId: String(this.config.productId) } });
  }

  async addPair() {
    if (this.busy() || eligibleQuantity(upsellState.cart, this.config.productId) !== 1) return;
    const variant = this.selectedVariant();
    if (!variant?.available) { upsellState.error = this.config.strings.unavailable; syncAll(); return; }
    upsellState.active = true;
    upsellState.error = '';
    upsellState.status = this.config.strings.checking;
    syncAll();
    this.lock();
    let deferred, mutationStarted = false;
    try {
      await Promise.allSettled([...CartItemsComponent.pendingQuantityUpdates, ...upsellState.pending]);
      const before = await this.request('cart.js');
      upsellState.cart = before;
      if (eligibleQuantity(before, this.config.productId) !== 1) {
        upsellState.error = this.config.strings.cart_changed;
        deferred = CartLinesUpdateEvent.createPromise();
        (this.isConnected ? this : document).dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: before.items.map((item) => ({ id: item.key, quantity: item.quantity })), promise: deferred.promise }));
        this.resolve(deferred, before, undefined, true);
        deferred = null;
        return;
      }
      const attempt = crypto.randomUUID();
      const size = this.config.sizeLabels[variant.options[this.sizeIndex]];
      const item = { id: Number(variant.id), quantity: 1, properties: { _barevon_offer: 'buy-one-get-one-free', _barevon_cart_attempt: attempt, ...(size ? { 'US size': size } : {}) } };
      deferred = CartLinesUpdateEvent.createPromise();
      (this.isConnected ? this : document).dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'cart', lines: [{ merchandiseId: String(variant.id), quantity: 1 }], promise: deferred.promise }));
      mutationStarted = true;
      let response = {}, addError;
      try { response = await this.request('cart/add.js', { items: [item], ...this.sectionPayload() }); } catch (error) { addError = error; }
      let cart = await this.request('cart.js');
      const check = inspectUpsell(cart, before, variant, attempt, this.config.productId, this.config.discountTitle);
      if (!check.valid) {
        if (!check.added.length && addError && !addError.serverRejected) throw new Error(this.config.strings.unconfirmed);
        if (check.added.length) {
          cart = await this.request('cart/update.js', { updates: Object.fromEntries(check.added.map((line) => [line.key, 0])), ...this.sectionPayload() });
          if (cart.items.some((line) => line.properties?._barevon_cart_attempt === attempt)) throw new Error(this.config.strings.unconfirmed);
        }
        upsellState.error = addError?.message || this.config.strings.discount_failed;
        this.resolve(deferred, cart, cart.sections, true);
      } else {
        upsellState.status = this.config.strings.added;
        this.resolve(deferred, cart, response.sections, false);
      }
      deferred = null;
    } catch (error) {
      upsellState.uncertain = mutationStarted;
      upsellState.error = mutationStarted ? this.config.strings.unconfirmed : error.message;
      deferred?.reject(error);
      (this.isConnected ? this : document).dispatchEvent(new CartErrorEvent({ error: upsellState.error, code: 'SERVICE_UNAVAILABLE' }));
    } finally {
      upsellState.active = false;
      if (upsellState.error) upsellState.status = '';
      this.unlock();
      syncAll();
    }
  }
}

if (!customElements.get('barevon-cart-upsell')) customElements.define('barevon-cart-upsell', BarevonCartUpsell);
